
import { useEffect, useCallback, useLayoutEffect } from 'react'

import styles from './index.module.css';

import {
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { ApolloClient, InMemoryCache, useQuery } from '@apollo/client';

import { authProvider } from "../../auth";
import { PATHS } from "../../paths";
import { MediaContextProvider, Media } from '../../mobile'

import { REFRESH_TOKEN } from '../../constants';
import { BACKEND_URL } from '../../config';
import { REFRESH_MUTATION } from '../../queries/getRefresh';
import { GET_ME } from '../../queries/getMe';

import { MobileNavigationFooter } from '../Mobile/Navigation';
import { useIsTeacher } from '../../hooks/isTeacher';
import { useIsAdmin } from '../../hooks/isAdmin';
import { useIsPartnerLeadership } from '../../hooks/isPartnerLeadership';

const client = new ApolloClient({
  uri: BACKEND_URL,
  cache: new InMemoryCache(),
});

// TODO: make a page to explain to a user why they are now on the login page
const PATH_FOR_FAILED_AUTH = `${PATHS.LOGIN}`;

function Layout() {
    let navigate = useNavigate();

    const path = useLocation();
    const isLoginPage = path.pathname === PATHS.LOGIN;
    const isClassesPage = path.pathname === PATHS.CLASSES;

    const isClassPage = /^\/classes\/\d+$/.test(path.pathname);

    const isTeacher = useIsTeacher();
    const isAdmin = useIsAdmin();
    const isPartnerLeadership = useIsPartnerLeadership();
    const isLeadershipPage = path.pathname === PATHS.LEADERSHIP;

    // Guard: a user with no working organisation set can't use the rest of the web app
    // (it assumes an org everywhere). Send them to the Organisations page, where they
    // pick one (or get logged out if they belong to none). Note the `me` query returns
    // a null organisation for unauthenticated requests too, so this is gated on being
    // authenticated and off the public/auth routes (and the Organisations page itself)
    // to avoid a redirect loop.
    const { loading: meLoading, error: meError, data: meData } = useQuery(GET_ME);

    useEffect(() => {
        const exemptPaths: string[] = [
            PATHS.ROOT, PATHS.LOGIN, PATHS.SIGNUP, PATHS.RESET_PASSWORD,
            PATHS.INFO, PATHS.PRIVACY_POLICY, PATHS.TERMS,
            PATHS.ORGANISATIONS, PATHS.LOGOUT,
        ];
        if (exemptPaths.includes(path.pathname)) return;
        if (!authProvider.isAuthenticated()) return;
        if (meLoading || meError) return;

        if (meData?.me && meData.me.organisation == null) {
            navigate(PATHS.ORGANISATIONS, { replace: true });
        }
    }, [meData, meLoading, meError, path.pathname, navigate]);

    const tryRefresh = useCallback(async () => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN);

        if (refreshToken) {
          const refreshRequest = await client.mutate({ mutation: REFRESH_MUTATION, variables: { refreshToken } });

          if (refreshRequest.errors) {
              await authProvider.signout();
              navigate(PATH_FOR_FAILED_AUTH, { replace: true });
          }

          if (refreshRequest?.data?.refresh?.success) {
              await authProvider.signin(refreshRequest?.data?.refresh?.token, refreshRequest?.data?.refresh?.refreshToken);
          } else {
              await authProvider.signout();
              navigate(PATH_FOR_FAILED_AUTH, { replace: true });
          }
        } else {
          navigate(PATH_FOR_FAILED_AUTH, { replace: true });
        }
    }, [navigate])

    useEffect(() => {

      if (isAdmin) {
          localStorage.setItem('theme', 'light');
      }
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
        document.documentElement.setAttribute('data-theme', 'light');
      } else {
        let theme = localStorage.getItem('theme')
        if (theme == null || theme != 'light' && theme != 'dark') {
            localStorage.setItem('theme', 'dark');
            theme = 'dark';
        }
        document.documentElement.setAttribute('data-theme', theme);
      }
    }, [isAdmin]);

    useEffect(() => {
        const intervalId = setInterval(() => tryRefresh(), 29 * 60 * 1000); // 29 minutes

        return () => clearInterval(intervalId);
    }, [tryRefresh]);

    useLayoutEffect(() => {
        const onVisibilityChange = async () => {
            const publicPaths = ['/signup', PATHS.PRIVACY_POLICY, PATHS.TERMS];
            if (document.visibilityState === 'visible' && !publicPaths.includes(document.location.pathname)) {
                // Tab reopened - user the refresh token
                await tryRefresh()
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [tryRefresh]);

  // Leadership users get the Leadership shell which manages its own nav and top bar
  if (isLeadershipPage && isPartnerLeadership && !isAdmin) {
    return <Outlet />;
  }

  return (
      <MediaContextProvider>
        <Media at='mobile'>
          {isTeacher ? (
            <div className={(isLoginPage || isClassesPage || isClassPage) ? styles.mobile : ''} >
              <Outlet />
              <MobileNavigationFooter />
            </div>
          ): (<Outlet />)}
        </Media>
        <Media greaterThan='mobile'>
            <Outlet />
        </Media>
      </MediaContextProvider>
  );
}

export { Layout };
